import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { AlertCircle, CheckCircle, Info, X, XCircle } from "react-feather";

// Popup padrão do site: aparece fixo na tela (não depende de scroll) e some sozinho.
// Use o hook `useToast()` em qualquer página para disparar avisos de ações
// (erro de pagamento, sucesso ao salvar, etc.) em vez de banners inline que só
// aparecem no topo da página.

const tones = {
  info: {
    border: "rgba(34, 184, 209, 0.32)",
    accent: "#22b8d1",
    icon: "#8FE3F2",
  },
  success: {
    border: "rgba(74, 222, 128, 0.32)",
    accent: "#4ade80",
    icon: "#86efac",
  },
  warning: {
    border: "rgba(250, 204, 21, 0.32)",
    accent: "#facc15",
    icon: "#fde047",
  },
  error: {
    border: "rgba(248, 113, 113, 0.32)",
    accent: "#f87171",
    icon: "#fca5a5",
  },
};

const iconByTone = {
  info: Info,
  success: CheckCircle,
  warning: AlertCircle,
  error: XCircle,
};

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(-10px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const Viewport = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 2147483000;
  display: grid;
  gap: 10px;
  width: min(380px, calc(100vw - 32px));
  pointer-events: none;

  @media (max-width: 640px) {
    top: 16px;
    right: 16px;
    left: 16px;
    width: auto;
  }
`;

const ToastCard = styled.div`
  pointer-events: auto;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 14px 14px 16px;
  border-radius: 12px;
  border: 1px solid ${({ $tone }) => tones[$tone].border};
  border-left: 3px solid ${({ $tone }) => tones[$tone].accent};
  background: #1f3050;
  box-shadow: 0 18px 40px rgba(10, 17, 30, 0.5);
  animation: ${slideIn} 0.2s ease;
`;

const IconBox = styled.div`
  flex: 0 0 auto;
  color: ${({ $tone }) => tones[$tone].icon};
  margin-top: 1px;
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
  display: grid;
  gap: 2px;
`;

const Title = styled.strong`
  color: #f4f7fc;
  font-size: 0.92rem;
`;

const Message = styled.div`
  color: #c7d2e8;
  font-size: 0.9rem;
  line-height: 1.5;
  overflow-wrap: break-word;
`;

const CloseButton = styled.button`
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: #7482a0;
  cursor: pointer;
  border-radius: 6px;
  transition: color 0.15s ease, background 0.15s ease;

  &:hover {
    color: #dbe6f5;
    background: rgba(148, 163, 184, 0.12);
  }
`;

const ToastContext = createContext(null);

let toastIdSeq = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismissToast = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const showToast = useCallback(
    (message, options = {}) => {
      const { tone = "info", title, duration = 6000 } = options;
      toastIdSeq += 1;
      const id = toastIdSeq;
      setToasts((current) => [...current, { id, message, tone, title }]);
      if (duration) {
        timers.current[id] = setTimeout(() => dismissToast(id), duration);
      }
      return id;
    },
    [dismissToast]
  );

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      {children}
      <Viewport aria-live="polite" aria-atomic="true">
        {toasts.map((toast) => {
          const Icon = iconByTone[toast.tone] || Info;
          return (
            <ToastCard key={toast.id} $tone={toast.tone} role="status">
              <IconBox $tone={toast.tone}>
                <Icon size={18} />
              </IconBox>
              <Content>
                {toast.title ? <Title>{toast.title}</Title> : null}
                {toast.message ? <Message>{toast.message}</Message> : null}
              </Content>
              <CloseButton type="button" onClick={() => dismissToast(toast.id)} aria-label="Fechar aviso">
                <X size={14} />
              </CloseButton>
            </ToastCard>
          );
        })}
      </Viewport>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Fallback silencioso caso algum componente seja renderizado fora do provider
    // (ex.: testes isolados) — evita quebrar a página.
    return { showToast: () => {}, dismissToast: () => {} };
  }
  return ctx;
}
